import { useEffect, useState } from 'react';
import type { CifrasIndicador, DefinicionIndicador, Dimension, Indicador } from '../../api/ops/indicadores';
import { Boton } from '../../componentes/Boton';
import { Desplegable } from '../../componentes/Desplegable';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { hoyLocal } from '../../logica/fechasCortas';
import { Barras, Grafico } from './Grafico';
import type { PropsApartado } from './Indicadores';
import {
    agrupacionesPara,
    agrupacionValida,
    area,
    BASE_FECHA,
    cambioDe,
    consultaDe,
    deCada,
    diaCorto,
    diasEntre,
    escribir,
    etiquetaPunto,
    LECTURA,
    mejorSiSube,
    motivoIncompleto,
    nombreDimension,
    notaIncompleto,
    PAPEL_EVENTO,
    PERIODOS,
    rangoDePeriodo,
    texto,
    textoComparacion,
    tonoDelCambio,
    valorDibujable,
    type Filtro,
} from './reglas';
import { useCifras } from './useCifras';

/**
 * Ficha de un indicador (7.2), en RV Panel: Gráfico (la cifra del periodo con su cambio, Día · Semana · Mes, la línea
 * con la de la comparación discontinua, «Ver como tabla», los días incompletos y el desglose por las dimensiones que
 * declara) y Definición (`getIndicator`: qué mide, cómo se calcula, base de fecha, versión, lectura, dimensiones y
 * eventos). La línea discontinua es otra `queryIndicator` con las fechas de `comparison` (la API solo da su total).
 */
export function FichaIndicador(props: PropsApartado & { indicador: Indicador | null; filtro: Filtro; onCerrar: () => void }) {
    const { indicador, filtro, onCerrar, movil, datos, puedeExportar, irA } = props;
    const [pestana, setPestana] = useState(0);
    const sede = filtro.sede === '' ? 'Todas las sedes' : (datos.sedes.find((s) => s.id === filtro.sede)?.nombre ?? '');
    const periodo = PERIODOS.find((p) => p.valor === filtro.periodo)?.texto ?? '';
    return (
        <PanelLateral
            abierto={indicador !== null}
            onCerrar={onCerrar}
            movil={movil}
            tamano="ficha"
            titulo={indicador?.name ?? ''}
            eyebrow={indicador === null ? '' : area(indicador.area).nombre}
            subtitulo={indicador === null ? '' : [periodo, sede, `versión ${String(indicador.version)}`].join(' · ')}
            icono={indicador === null ? 'chart-line' : area(indicador.area).icono}
            accion={puedeExportar ? 'Exportar' : ''}
            accionIcono="download"
            onAccion={() => {
                if (indicador !== null) {
                    irA('exportaciones', { nueva: indicador.code });
                }
            }}
        >
            {indicador !== null && (
                <div className="rv-ind-ficha">
                    <Segmentos
                        etiqueta="Partes de la ficha"
                        movil={movil}
                        items={[
                            { etiqueta: 'Gráfico', icono: 'chart-line' },
                            { etiqueta: 'Definición', icono: 'book-open' },
                        ]}
                        valor={pestana}
                        onCambio={setPestana}
                    />
                    {pestana === 0 ? <PestanaGrafico key={indicador.code} {...props} indicador={indicador} /> : <PestanaDefinicion key={indicador.code} {...props} indicador={indicador} />}
                </div>
            )}
        </PanelLateral>
    );
}

function PestanaGrafico({ ops, negocio, indicador, filtro, datos }: PropsApartado & { indicador: Indicador; filtro: Filtro }) {
    const hoy = hoyLocal();
    const r = rangoDePeriodo(filtro.periodo, hoy, filtro.fechas);
    const dias = diasEntre(r.desde, r.hasta);
    const [agrupacion, setAgrupacion] = useState(agrupacionValida(filtro.agrupacion, dias));
    const [tabla, setTabla] = useState(false);
    const [desglose, setDesglose] = useState<Dimension | ''>('');
    const opciones = agrupacionesPara(dias);
    const consulta = consultaDe({ ...filtro, agrupacion }, hoy);
    const { cifras, error } = useCifras(ops, negocio, indicador.code, consulta);
    const comparacion = cifras?.comparison ?? null;
    const consultaAntes = consulta === null || comparacion === null || comparacion.mode === 'none' ? null : { ...consulta, from: comparacion.from, to: comparacion.to, compare: 'none' as const };
    const antes = useCifras(ops, negocio, indicador.code, consultaAntes).cifras;
    const consultaDesglose = consulta === null || desglose === '' ? null : { ...consulta, compare: 'none' as const, breakdown: desglose };
    const desglosado = useCifras(ops, negocio, indicador.code, consultaDesglose);

    if (error !== null) {
        return <p className="rv-ca-nota">No se han podido leer las cifras. Prueba otra vez en un momento.</p>;
    }
    if (cifras === null) {
        return <p className="rv-ca-nota rv-ca-nota--gris">Cargando las cifras…</p>;
    }
    const unidad = cifras.unit ?? indicador.unit;
    const serie = cifras.series.find((s) => (s.dimension_value ?? null) === null) ?? cifras.series[0];
    const otrasMonedas = cifras.series.filter((s) => s !== serie && (s.dimension_value ?? null) === null);
    const puntos = (serie?.points ?? []).map((p) => ({ etiqueta: etiquetaPunto(p.period_start, cifras.granularity), valor: valorDibujable(p), incompleto: motivoIncompleto(p) !== null, punto: p }));
    const serieAntes = antes?.series.find((s) => (s.dimension_value ?? null) === null && (serie?.currency == null || s.currency === serie.currency));
    const e = escribir(unidad, serie?.total);
    const cambio = cambioDe(unidad, comparacion, serie ?? null);
    const primero = puntos.find((p) => p.incompleto);
    const motivo = primero === undefined ? null : motivoIncompleto(primero.punto);
    const escribirEje = (v: number) => (unidad === 'money' ? v.toLocaleString('es-ES', { maximumFractionDigits: 0 }) : unidad === 'percent' ? `${v.toLocaleString('es-ES', { maximumFractionDigits: 1 })} %` : v.toLocaleString('es-ES', { maximumFractionDigits: 1 }));

    return (
        <div className="rv-ind-ficha__cuerpo">
            <div className="rv-ind-caja">
                <div className="rv-ind-caja__cabeza">
                    <div>
                        <span className="rv-ind-caja__titulo">{indicador.name}</span>
                        <span className="rv-ind-tarjeta__cifra">
                            <strong>{e.cifra}</strong>
                            {e.unidad !== '' && <small>{e.unidad}</small>}
                            {cambio !== null && <em className={`rv-ind-cambio rv-ind-cambio--${tonoDelCambio(cambio.sentido, mejorSiSube(indicador))}`}>{cambio.texto}</em>}
                        </span>
                        {comparacion !== null && comparacion.mode !== 'none' && (
                            <small className="rv-ind-gris">
                                {`frente a ${textoComparacion(comparacion.mode)} (${texto(unidad, comparacion.series.find((s) => (s.dimension_value ?? null) === null)?.total ?? null)})`}
                            </small>
                        )}
                        {cifras.own_scope_applied === true && <small className="rv-ind-gris">Solo tus cifras.</small>}
                    </div>
                    {opciones.length > 1 && (
                        <Filtros
                            etiqueta="Agrupar por"
                            items={opciones.map((g) => ({ etiqueta: g.texto }))}
                            valor={Math.max(0, opciones.findIndex((g) => g.valor === agrupacion))}
                            onCambio={(i) => { setAgrupacion(opciones[i]?.valor ?? agrupacion); }}
                        />
                    )}
                </div>
                {serieAntes !== undefined && (
                    <div className="rv-ind-leyenda" aria-hidden="true">
                        <span className="rv-ind-leyenda__ahora">Este periodo</span>
                        <span className="rv-ind-leyenda__antes">{comparacion?.mode === 'previous_year' ? 'El año pasado' : 'Periodo anterior'}</span>
                    </div>
                )}
                {tabla ? (
                    <table className="rv-ind-tabla">
                        <thead>
                            <tr>
                                <th scope="col">Periodo</th>
                                <th scope="col">{indicador.name}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {puntos.map((p) => (
                                <tr key={p.punto.period_start}>
                                    <td>
                                        {p.etiqueta}
                                        {p.incompleto && <small className="rv-ind-gris"> · sin datos completos</small>}
                                    </td>
                                    <td>{texto(unidad, p.punto, true)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <Grafico puntos={puntos} anteriores={serieAntes?.points.map(valorDibujable)} escribir={escribirEje} etiqueta={`${indicador.name}: ${texto(unidad, serie?.total)}`} />
                )}
                <button type="button" className="rv-ind-mas" onClick={() => { setTabla((t) => !t); }}>
                    <Icono nombre={tabla ? 'chart-line' : 'table'} />
                    {tabla ? 'Ver como gráfico' : 'Ver como tabla'}
                </button>
                {otrasMonedas.map((s) => (
                    <small key={s.currency ?? ''} className="rv-ind-gris">{`También en ${s.currency ?? ''}: ${texto(unidad, s.total)}`}</small>
                ))}
            </div>
            {primero !== undefined && motivo !== null && (
                <p className="rv-ind-aviso">
                    <Icono nombre="circle-alert" />
                    <span>{notaIncompleto(motivo, primero.etiqueta)}</span>
                </p>
            )}
            {indicador.dimensions.length > 0 && (
                <div className="rv-ind-caja">
                    <Desplegable
                        etiqueta="Desglosar por"
                        valor={desglose}
                        opciones={[{ valor: '', texto: 'Sin desglose' }, ...indicador.dimensions.map((d) => ({ valor: d, texto: nombreDimension(d) }))]}
                        onChange={(v) => { setDesglose(v as Dimension | ''); }}
                    />
                    {desglose !== '' && <Desglose cifras={desglosado.cifras} error={desglosado.error} unidad={unidad} sedes={datos.sedes} dimension={desglose} />}
                </div>
            )}
        </div>
    );
}

function Desglose({ cifras, error, unidad, sedes, dimension }: { cifras: CifrasIndicador | null; error: unknown; unidad: Indicador['unit']; sedes: { id: string; nombre: string }[]; dimension: Dimension }) {
    if (error !== null) {
        return <p className="rv-ca-nota">No se ha podido leer el desglose.</p>;
    }
    if (cifras === null) {
        return <p className="rv-ca-nota rv-ca-nota--gris">Cargando el desglose…</p>;
    }
    const filas = cifras.series
        .filter((s) => (s.dimension_value ?? null) !== null)
        .map((s) => ({
            clave: `${s.dimension_value ?? ''}-${s.currency ?? ''}`,
            // Sin `label`, la sede se resuelve con las del negocio; el resto sale con su id (pregunta abierta).
            nombre: s.label ?? (dimension === 'location' ? sedes.find((x) => x.id === s.dimension_value)?.nombre : undefined) ?? s.dimension_value ?? '',
            valor: valorDibujable(s.total),
            cifra: texto(unidad, s.total),
            detalle: unidad === 'percent' ? deCada(s.total) : null,
        }))
        .sort((a, b) => (b.valor ?? 0) - (a.valor ?? 0));
    if (filas.length === 0) {
        return <p className="rv-ca-nota rv-ca-nota--gris">No hay cifras que desglosar en este periodo.</p>;
    }
    return <Barras filas={filas} />;
}

function PestanaDefinicion({ ops, negocio, indicador, navegar }: PropsApartado & { indicador: Indicador }) {
    const [definicion, setDefinicion] = useState<DefinicionIndicador | null>(null);
    const [error, setError] = useState(false);
    useEffect(() => {
        let vivo = true;
        ops.getIndicator(negocio, indicador.code)
            .then((d) => {
                if (vivo) {
                    setDefinicion(d);
                }
            })
            .catch(() => {
                if (vivo) {
                    setError(true);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, indicador.code]);

    if (error) {
        return <p className="rv-ca-nota">No se ha podido leer la definición.</p>;
    }
    if (definicion === null) {
        return <p className="rv-ca-nota rv-ca-nota--gris">Cargando la definición…</p>;
    }
    const lineas: { k: string; v: string }[] = [
        ...(definicion.description === undefined || definicion.description === '' ? [] : [{ k: 'Qué mide', v: definicion.description }]),
        { k: 'Cómo se calcula', v: definicion.calculation },
        ...(definicion.date_basis === undefined ? [] : [{ k: 'Cuenta el día', v: BASE_FECHA[definicion.date_basis] ?? definicion.date_basis }]),
        { k: 'Versión', v: definicion.current_since === undefined ? String(definicion.version) : `${String(definicion.version)} · vigente desde el ${diaCorto(definicion.current_since.slice(0, 10), true)}` },
        { k: 'Lectura', v: LECTURA[definicion.read_permission] ?? definicion.read_permission },
        { k: 'Desglose', v: definicion.dimensions.length === 0 ? 'Solo el total' : definicion.dimensions.map(nombreDimension).join(', ') },
    ];
    return (
        <div className="rv-ind-ficha__cuerpo">
            <dl className="rv-ind-definicion">
                {lineas.map((l) => (
                    <div key={l.k}>
                        <dt>{l.k}</dt>
                        <dd>{l.v}</dd>
                    </div>
                ))}
                <div>
                    <dt>Eventos</dt>
                    <dd>
                        <ul>
                            {definicion.events.map((ev) => (
                                <li key={`${ev.event}-${ev.role}`}>
                                    <code>{ev.event}</code> <span className="rv-ind-gris">{PAPEL_EVENTO[ev.role] ?? ev.role}</span>
                                </li>
                            ))}
                        </ul>
                    </dd>
                </div>
            </dl>
            {definicion.detail_link != null && definicion.detail_link !== '' && (
                <Boton tipo="secundario" icono="arrow-right" onClick={() => { navegar(definicion.detail_link ?? '/'); }}>
                    Ver el detalle
                </Boton>
            )}
        </div>
    );
}
